import { useEffect } from "react";

import { NetworkStatus, useQuery } from "@apollo/client";
import uniqBy from "lodash/uniqBy";

import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { GET_DEVICES } from "../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../common/variables/common";
import { errorNotification } from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import client from "../../../configs/apolloClient";
import type { GetDevicesQuery, GetDevicesQueryVariables } from "./../../../API";

export interface useGetDevicesInterface {
  locationId?: string;
  locationName?: string;
  nodeId?: string;
  nodeName?: string;
}

export const DEVICES_TABLE_LIMIT = 25;

export const useGetDevices = () => {
  const locationVariable = useSelectedLocationDropdown();

  const locationId = locationVariable?.value;

  const selectedCustomerId = useCustomerIdGuard();

  const { data, fetchMore, loading, networkStatus, refetch } = useQuery<
    GetDevicesQuery,
    GetDevicesQueryVariables
  >(GET_DEVICES, {
    variables: {
      limit: DEFAULT_LIMIT_COUNT,
      customerId: selectedCustomerId,
      locationId,
    },
    skip: !selectedCustomerId,
    notifyOnNetworkStatusChange: true,
  });

  // Load all pages when the initial data is fetched and there's a nextToken
  useEffect(() => {
    if (!data?.getDevices?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getDevices.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: DEFAULT_LIMIT_COUNT,
              customerId: selectedCustomerId,
              locationId,
              nextToken: currentNextToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }

              const uniqueDevices = uniqBy(
                [
                  ...(prev.getDevices?.items ?? []),
                  ...(fetchMoreResult.getDevices?.items ?? []),
                ],
                item => item?.id
              );

              return {
                getDevices: {
                  ...fetchMoreResult.getDevices,
                  items: uniqueDevices,
                },
              };
            },
          });

          currentNextToken = response.data?.getDevices?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional device pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [data?.getDevices?.nextToken, selectedCustomerId, locationId]);

  useEffect(() => {
    return () => {
      client.cache.evict({
        id: "ROOT_QUERY",
        fieldName: "getDevices",
        broadcast: false,
      });
    };
  }, []);

  const devicesLoading =
    loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  return { data, loading: devicesLoading, refetch };
};
